import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Search, Plus, Edit, Trash2, Check, X, BookOpen, AlertCircle, Eye } from '../../lib/lucide-proxy';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Modal from '../../components/ui/Modal';
import { supabase } from '../../config/supabase';
import toast from 'react-hot-toast';
import LoadingSpinner from '../../components/ui/LoadingSpinner';

const AdminEncyclopedia = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [formData, setFormData] = useState({
    name: '', style: '', description: '', abv_range: '', ibu_range: '', pairing: '', image_url: '', is_approved: true
  });

  useEffect(() => {
    fetchItems();
  }, []);

  const fetchItems = async () => {
    try {
      const { data, error } = await supabase
        .from('beer_encyclopedia')
        .select('*, author:users(full_name)')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setItems(data || []);
    } catch (error) {
      console.error('Error fetching encyclopedia:', error);
      toast.error('Erro ao carregar itens.');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (id, status) => {
    try {
      const { error } = await supabase
        .from('beer_encyclopedia')
        .update({ is_approved: status })
        .eq('id', id);

      if (error) throw error;
      toast.success(status ? 'Aprovado com sucesso!' : 'Desaprovado.');
      fetchItems();
    } catch (error) {
      toast.error('Erro ao atualizar status.');
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Tem certeza que deseja excluir?')) return;
    try {
      const { error } = await supabase
        .from('beer_encyclopedia')
        .delete()
        .eq('id', id);

      if (error) throw error;
      toast.success('Excluído com sucesso.');
      fetchItems();
    } catch (error) {
      toast.error('Erro ao excluir.');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (selectedItem) {
        const { error } = await supabase
          .from('beer_encyclopedia')
          .update(formData)
          .eq('id', selectedItem.id);
        if (error) throw error;
        toast.success('Atualizado!');
      } else {
        const { error } = await supabase
          .from('beer_encyclopedia')
          .insert([formData]);
        if (error) throw error;
        toast.success('Criado!');
      }
      setIsModalOpen(false);
      fetchItems();
      resetForm();
    } catch (error) {
      toast.error('Erro ao salvar.');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (item) => {
    setSelectedItem(item);
    setFormData({
      name: item.name,
      style: item.style,
      description: item.description,
      abv_range: item.abv_range || '',
      ibu_range: item.ibu_range || '',
      pairing: item.pairing || '',
      image_url: item.image_url || '',
      is_approved: item.is_approved
    });
    setIsModalOpen(true);
  };

  const resetForm = () => {
    setSelectedItem(null);
    setFormData({
      name: '', style: '', description: '', abv_range: '', ibu_range: '', pairing: '', image_url: '', is_approved: true
    });
  };

  const filteredItems = items.filter(item => 
    item.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
    item.style.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const pendingCount = items.filter(i => !i.is_approved).length;

  return (
    <>
      <Helmet><title>Gerenciar Enciclopédia - Admin</title></Helmet>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
          <div>
            <h2 className="text-2xl font-bold text-dark-900">Enciclopédia Cervejeira</h2>
            <p className="text-dark-600">Gerencie estilos e aprovações.</p>
          </div>
          <Button variant="primary" onClick={() => { resetForm(); setIsModalOpen(true); }}>
            <Plus className="w-4 h-4 mr-2" /> Novo Item
          </Button>
        </div>

        {pendingCount > 0 && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 flex items-start">
            <AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5 mr-3 flex-shrink-0" />
            <div>
              <h3 className="text-sm font-bold text-yellow-800">Aprovações Pendentes</h3>
              <p className="text-sm text-yellow-700">
                Existem {pendingCount} contribuições aguardando sua aprovação.
              </p>
            </div>
          </div>
        )}

        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="p-4 border-b border-gray-200 bg-gray-50">
            <div className="relative max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input 
                type="text" 
                placeholder="Buscar por nome ou estilo..." 
                value={searchTerm} 
                onChange={(e) => setSearchTerm(e.target.value)} 
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:outline-none" 
              />
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Cerveja/Estilo</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Autor</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Ações</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredItems.map((item) => (
                  <tr key={item.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        {item.image_url && <img src={item.image_url} alt="" className="w-8 h-8 rounded bg-gray-100 object-cover mr-3" />}
                        <div>
                          <div className="text-sm font-medium text-gray-900">{item.name}</div>
                          <div className="text-xs text-gray-500">{item.style}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {item.author?.full_name || 'Admin'}
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 rounded-full text-xs font-bold ${item.is_approved ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                        {item.is_approved ? 'Aprovado' : 'Pendente'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right text-sm font-medium">
                      <div className="flex items-center justify-end space-x-2">
                        {!item.is_approved && (
                          <button onClick={() => handleApprove(item.id, true)} className="p-1 text-green-600 hover:bg-green-50 rounded" title="Aprovar">
                            <Check className="w-4 h-4" />
                          </button>
                        )}
                        <button onClick={() => handleEdit(item)} className="p-1 text-blue-600 hover:bg-blue-50 rounded" title="Editar">
                          <Edit className="w-4 h-4" />
                        </button>
                        <button onClick={() => handleDelete(item.id)} className="p-1 text-red-600 hover:bg-red-50 rounded" title="Excluir">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {filteredItems.length === 0 && (
              <div className="text-center py-8 text-gray-500">Nenhum item encontrado.</div>
            )}
          </div>
        </div>
      </div>

      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} title={selectedItem ? "Editar Item" : "Novo Item"} size="lg">
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <Input label="Nome" required value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} />
            <Input label="Estilo" required value={formData.style} onChange={e => setFormData({...formData, style: e.target.value})} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Descrição</label>
            <textarea className="input-field" rows={4} required value={formData.description} onChange={e => setFormData({...formData, description: e.target.value})} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Input label="ABV Range" value={formData.abv_range} onChange={e => setFormData({...formData, abv_range: e.target.value})} placeholder="4.5% - 5.0%" />
            <Input label="IBU Range" value={formData.ibu_range} onChange={e => setFormData({...formData, ibu_range: e.target.value})} placeholder="20 - 30" />
          </div>
          <Input label="Harmonização" value={formData.pairing} onChange={e => setFormData({...formData, pairing: e.target.value})} />
          <Input label="URL Imagem" value={formData.image_url} onChange={e => setFormData({...formData, image_url: e.target.value})} />
          
          <div className="flex items-center pt-2">
            <input 
              type="checkbox" 
              id="approved" 
              checked={formData.is_approved} 
              onChange={e => setFormData({...formData, is_approved: e.target.checked})} 
              className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
            />
            <label htmlFor="approved" className="ml-2 text-sm text-gray-700">Aprovado e Visível</label>
          </div>

          <div className="flex justify-end space-x-2 pt-4">
            <Button variant="secondary" onClick={() => setIsModalOpen(false)}>Cancelar</Button>
            <Button type="submit" variant="primary">Salvar</Button>
          </div>
        </form>
      </Modal>
    </>
  );
};

export default AdminEncyclopedia;